import React, { useState, useRef, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.css";
import { Form, Button, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { storage, useAuth, auth } from "../../base";
import "../uploadImage/uploadImage.css";
import { toast } from "react-toastify";
import { useProjectLimits } from '../../hooks/useProjectLimits';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../base';
import featureFlags from '../../config/featureFlags';

function UploadImage(props) {
  const currentUser = useAuth();
  const loggeInUser = auth.currentUser.uid;
  const projectNameRef = useRef();
  const fileInputRef = useRef(); // Add ref for file input
  const [canvasImage, setCanvasImage] = useState("");
  const [imageName, setImageName] = useState("");
  const [progress, setProgress] = useState(0);
  const [imageURL, setImageURL] = useState("");
  const [projectName, setProjectName] = useState("");
  const [error, setError] = useState("");
  const [goUpload, setGoUpload] = useState(false);
  const [uploadSuccessful, setUploadSuccessful] = useState(false);
  const [isDuplicateName, setIsDuplicateName] = useState(false); // ✅ Track duplicate state
  const [isProjectNameEmpty, setIsProjectNameEmpty] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);

  // Get project limits data
  const { projectCount, projectLimit, isLoading, canCreateProject, isAtLimit } = useProjectLimits(currentUser, userData);

  const { t } = useTranslation(["translation"]);

  // Real-time listener for user data
  useEffect(() => {
    if (!currentUser || currentUser.email.startsWith("guest_")) {
      setUserData(null);
      return;
    }

    const userDocRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        setUserData(doc.data());
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    projectNameRef.current.focus();
  }, []);

  // ✅ Helper function to check if project name is duplicate
  const isProjectNameDuplicate = (name) => {
    if (!name) return false;
    return props.projects?.some(
      project => project.projectName.replaceAll("_", " ").toLowerCase() === name.toLowerCase() &&
                 project.userId === loggeInUser
    );
  };

  const handleProjectName = (e) => {
    const inputName = e.target.value.trim();
    
    // Always update the project name state (allow user to type)
    setProjectName(inputName);

    setIsProjectNameEmpty(!inputName);
    setIsDuplicateName(inputName ? isProjectNameDuplicate(inputName) : false);
    projectNameRef.current.focus();
    return;
  };

  const handleCameraClick = () => {
    console.log("Camera clicked!", { projectName, progress });
    
    if (!projectName) {
      setIsProjectNameEmpty(true);
      return;
    }
    
    // ✅ Check project limits before proceeding (only if limits are enabled)
    if (featureFlags.PROJECT_LIMITS_ENABLED && !canCreateProject && !isLoading) {
      const planName = userData?.planName?.replace('_plan', '').replace('_', ' ') || 'current plan';
      toast.error(
        t('projectLimitReached', {
          current: projectCount,
          limit: projectLimit,
          plan: planName,
          defaultValue: `Project limit reached! You have ${projectCount}/${projectLimit} projects with your ${planName}. Please upgrade your plan to create more projects.`
        }),
        { position: 'top-center', autoClose: 5000 }
      );
      return;
    }
    
    // ✅ Check for duplicate project names before proceeding
    if (isProjectNameDuplicate(projectName)) {
      setIsDuplicateName(true);
      toast.warn(t("upload-duplicateProjectName"), {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }
    
    // Trigger the file input click using ref
    if (fileInputRef.current) {
      console.log("Triggering file input click");
      fileInputRef.current.click();
    } else {
      console.log("File input ref not found");
    }
  };

  const handleDefaultImageSelect = (imageSrc, imageName) => {
    if (isSubmittingProject) {
      return;
    }

    if (!projectName) {
      setIsProjectNameEmpty(true);
      return; // If checkAndSetProjectName returns false, it means no project name was set
    }
    
    // ✅ Check project limits before proceeding (only if limits are enabled)
    if (featureFlags.PROJECT_LIMITS_ENABLED && !canCreateProject && !isLoading) {
      const planName = userData?.planName?.replace('_plan', '').replace('_', ' ') || 'current plan';
      toast.error(
        t('projectLimitReached', {
          current: projectCount,
          limit: projectLimit,
          plan: planName,
          defaultValue: `Project limit reached! You have ${projectCount}/${projectLimit} projects with your ${planName}. Please upgrade your plan to create more projects.`
        }),
        { position: 'top-center', autoClose: 5000 }
      );
      return;
    }
    
    // ✅ Check for duplicate project names before proceeding
    if (isProjectNameDuplicate(projectName)) {
      setIsDuplicateName(true);
      toast.warn(t("upload-duplicateProjectName"), {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    setImageURL(imageSrc);
    setCanvasImage(imageSrc);
    setImageName(imageName);
    setGoUpload(true);
    setIsSubmittingProject(true);
    handleUpload(imageSrc, imageName).catch((error) => {
      console.error("Error uploading default image:", error);
      setIsSubmittingProject(false);
    });
  };

  const validateAndSetSelectedFile = (file) => {
    setError("");
    setCanvasImage("");
    setImageName("");
    if (!file) {
      return;
    }

    const fileMaxWeight = 1024 * 1024 * 16;
    const fileWeight = file["size"];
    const fileType = file["type"];
    const validImageType = [
      "image/jpg",
      "image/jpeg",
      "image/gif",
      "image/png",
    ];

    if (!validImageType.includes(fileType) || fileWeight > fileMaxWeight) {
      if (fileWeight > fileMaxWeight) {
        setError(t("uploadImage.errorWeight"));
      }
      if (!validImageType.includes(fileType)) {
        setError(t("uploadImage.errorType"));
      }
      return;
    }

    setGoUpload(true);
    setCanvasImage(file);
    setImageName(file.name);
    setError("");
    setImageURL(URL.createObjectURL(file));
  };

  const handleChange = (e) => {
    const file = e.target.files[0];
    validateAndSetSelectedFile(file);
  };

  const handleDropZoneClick = () => {
    handleCameraClick();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!projectName || isAtLimit) return;
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (!projectName) {
      setIsProjectNameEmpty(true);
      return;
    }

    if (featureFlags.PROJECT_LIMITS_ENABLED && !canCreateProject && !isLoading) {
      const planName = userData?.planName?.replace('_plan', '').replace('_', ' ') || 'current plan';
      toast.error(
        t('projectLimitReached', {
          current: projectCount,
          limit: projectLimit,
          plan: planName,
          defaultValue: `Project limit reached! You have ${projectCount}/${projectLimit} projects with your ${planName}. Please upgrade your plan to create more projects.`
        }),
        { position: 'top-center', autoClose: 5000 }
      );
      return;
    }

    validateAndSetSelectedFile(e.dataTransfer?.files?.[0]);
  };

  const handleUpload = (canvasImage, imageName) => {
    // ✅ Final check for duplicate project names before upload
    if (isProjectNameDuplicate(projectName)) {
      setIsDuplicateName(true);
      toast.error(t("upload-duplicateProjectName"), {
        position: toast.POSITION.TOP_CENTER,
      });
      return Promise.reject(new Error("Duplicate project name"));
    }
    
    // props.setIsEvaluating(true)
    // props.updateProgress("Uploading and saving image..."); // ✅ Step 1
    props.setImageName(imageName)
    return new Promise((resolve, reject) => {
      if (!canvasImage || !imageName) {
        console.error('canvasImage or imageName is undefined');
        setIsSubmittingProject(false);
        reject(new Error('canvasImage or imageName is undefined'));
        return;
      }

      setIsSubmittingProject(true);
      console.time("NewProject.uploadSourceImage");
      props.showLoader();

      // props.setIsEvaluating(true) - removed as part of AI feature removal
      props.updateProgress("uploading"); // ✅ Step 1

      const uploadImage = (file, imageName) => {
        const imageRef = storage.ref(`usersProjectsImages/${loggeInUser}/canvasImages/${projectName.replaceAll(" ", "_") + "_" + loggeInUser}`);
        const uploadTask = imageRef.put(file);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            if (snapshot.totalBytes > 0) {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setProgress(progress);
            } else {
              console.warn('Total bytes is zero');
            }
          },
          (error) => {
            console.log(error);
            console.timeEnd("NewProject.uploadSourceImage");
            setIsSubmittingProject(false);
            // props.hideLoader();
            reject(error);
          },
          () => {
            imageRef.getDownloadURL()
              .then((url) => {
                console.timeEnd("NewProject.uploadSourceImage");
                setImageURL(url); //url for canvas background
                props.setBackgroundImageStorageUrl(url);
                props.setProjectName(projectName);
                props.setNewProjectCreationName(projectName);
                props.hideLoader();
                setUploadSuccessful(true); // Set upload successful flag
                props.closeUpload(false);

                if (props.onImageUploadSuccess) {
                  props.onImageUploadSuccess(url, projectName);
                }
                resolve(url); // Return the URL upon successful upload.
              })
              .catch((error) => {
                console.log(error);
                console.timeEnd("NewProject.uploadSourceImage");
                setIsSubmittingProject(false);
                // props.hideLoader();
                reject(error);
              });
          }
        );

        setProgress(0);
        props.closeAccount(false);
      };

      if (typeof canvasImage === 'string') { // Default image selected
        fetch(canvasImage)
          .then((response) => response.blob())
          .then((blob) => {
            const file = new File([blob], imageName, { type: blob.type });
            uploadImage(file, imageName);
          })
          .catch((error) => {
            console.log(error);
            console.timeEnd("NewProject.uploadSourceImage");
            setIsSubmittingProject(false);
            reject(error);
          });
      } else { // User-selected file
        uploadImage(canvasImage, imageName);
      }
    });
  };

  const handleClose = () => {
    if (!uploadSuccessful) {
      // Do not mutate the currently opened project when canceling new project creation.
      props.setNewProjectCreationName("");
      if (props.setIsCreatingNewProject) {
        props.setIsCreatingNewProject(false);
      }
      if (props.onCancelNewProject) {
        props.onCancelNewProject();
      }
    }
    props.closeUpload(false);
  };

  return (
    <Modal
      show={true}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      backdropClassName="new-project-upload-backdrop"
      className="new-project-upload-modal-root"
      dialogClassName="new-project-upload-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t("uploadImage.startProject")}
          {featureFlags.PROJECT_LIMITS_ENABLED && !isLoading && currentUser && !currentUser.email.startsWith("guest_") && (
            <small className="text-muted ms-2">
              ({projectCount}/{projectLimit} projects used)
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Project Limit Warning */}
        {featureFlags.PROJECT_LIMITS_ENABLED && !isLoading && currentUser && !currentUser.email.startsWith("guest_") && (
          <>
            {isAtLimit && (
              <div className="alert alert-danger">
                <strong>{t("uploadImage.projectLimitReachedTitle", { defaultValue: "Project Limit Reached!" })}</strong><br />
                {t("uploadImage.projectLimitReachedBody", {
                  current: projectCount,
                  limit: projectLimit,
                  plan: userData?.planName?.replace('_plan', '').replace('_', ' ') || t("uploadImage.currentPlan", { defaultValue: "current" }),
                  defaultValue: `You have reached your project limit (${projectCount}/${projectLimit}) for your ${userData?.planName?.replace('_plan', '').replace('_', ' ') || 'current'} plan. Please upgrade your plan to create more projects.`
                })}
              </div>
            )}
            {!isAtLimit && projectCount >= projectLimit * 0.8 && (
              <div className="alert alert-warning">
                <strong>{t("uploadImage.projectLimitWarningTitle", { defaultValue: "Approaching Project Limit" })}</strong><br />
                {t("uploadImage.projectLimitWarningBody", {
                  current: projectCount,
                  limit: projectLimit,
                  plan: userData?.planName?.replace('_plan', '').replace('_', ' ') || t("uploadImage.currentPlan", { defaultValue: "current" }),
                  defaultValue: `You have used ${projectCount} of ${projectLimit} projects in your ${userData?.planName?.replace('_plan', '').replace('_', ' ') || 'current'} plan.`
                })}
              </div>
            )}
          </>
        )}

        <div className="projectNameInput">
          <Form>
            <Form.Group className="mb-1" controlId="exampleForm.ControlInput1">
              <Form.Label>{t("uploadImage.projectNameLabel")}</Form.Label>
              <Form.Control
                className={`inputProjectName mb-1 ${(isDuplicateName || isProjectNameEmpty) ? 'is-invalid' : ''}`}
                type="text"
                placeholder={t("uploadImage.projectNamePlaceholder", { defaultValue: "Project 1" })}
                ref={projectNameRef}
                autoComplete="off"
                name="pName"
                onChange={handleProjectName}
                style={{
                  borderColor: (isDuplicateName || isProjectNameEmpty) ? '#dc3545' : '',
                  boxShadow: (isDuplicateName || isProjectNameEmpty) ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : ''
                }}
              />
              {isProjectNameEmpty && (
                <div className="invalid-feedback d-block">
                  {t("upload-enterprojectname")}
                </div>
              )}
              {isDuplicateName && !isProjectNameEmpty && (
                <div className="invalid-feedback d-block">
                  {t("upload-duplicateProjectName")}
                </div>
              )}
            </Form.Group>
          </Form>
        </div>

        <div className="text-center">
          <div className="labelContainer w-100">
            <div className="uploadFromDevice">
              <h3>{t("uploadImage.selectImage")}</h3>
              <br />
              <input
                ref={fileInputRef}
                disabled={!projectName || progress || isSubmittingProject}
                id="inputTag"
                type="file"
                size="sm"
                onChange={handleChange}
                style={{ display: "none" }}
                accept="image/*"
              />
              <br />

              {/* Move the File Name Here */}
              <div className="image-name">{imageName}</div>

              <div
                className={`upload-dropzone ${isDragActive ? 'drag-active' : ''} ${isAtLimit ? 'disabled' : ''}`}
                onClick={isAtLimit ? undefined : handleDropZoneClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={isAtLimit ? undefined : handleDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isAtLimit) {
                    e.preventDefault();
                    handleDropZoneClick();
                  }
                }}
              >
                <div className="upload-dropzone-title">
                  {t("uploadImage.dropzoneTitle", { defaultValue: "Drop your facade image here" })}
                </div>
                <div className="upload-dropzone-subtitle">
                  {t("uploadImage.dropzoneSubtitle", { defaultValue: "or use the file finder to browse your device" })}
                </div>
                <Button
                  type="button"
                  className="upload-dropzone-button"
                  disabled={isAtLimit || !projectName || !!progress || isSubmittingProject}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCameraClick();
                  }}
                >
                  {t("uploadImage.browseFiles", { defaultValue: "Browse files" })}
                </Button>
              </div>

              <div className="tooltip-container">
                {!imageURL && <div className="arrow">⬇</div>}
                <div 
                  className="faContainer" 
                  onClick={isAtLimit ? undefined : handleCameraClick} 
                  style={{ 
                    cursor: isAtLimit ? 'not-allowed' : 'pointer',
                    opacity: isAtLimit ? 0.5 : 1,
                    filter: isAtLimit ? 'grayscale(50%)' : 'none'
                  }}
                >
                  {imageURL ? (
                    <img className="thumbnail" src={imageURL} alt={t("uploadImage.thumbnailAlt", { defaultValue: "Project preview" })} />
                  ) : (
                    <i className="fa fa-2x fa-camera"></i>
                  )}
                </div>
              </div>

              <div className="buttonContainer text-center">
                <Button
                  className="btn btn-secondary mt-2"
                  disabled={!goUpload || progress || isSubmittingProject}
                  onClick={() => {
                    handleUpload(canvasImage, imageName).catch((error) => {
                      console.error("Error uploading image:", error);
                    });
                  }}
                >
                  {isSubmittingProject ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      {t("uploadImage.creatingProject", { defaultValue: "Creating project..." })}
                    </>
                  ) : (
                    t("uploadImage.upload")
                  )}
                </Button>
              </div>
              {isSubmittingProject && (
                <div className="upload-submit-feedback">
                  <Spinner animation="border" size="sm" role="status" />
                  <span>
                    {t("uploadImage.projectCreationPending", {
                      defaultValue: "Uploading the image and preparing project thumbnails..."
                    })}
                  </span>
                </div>
              )}
            </div>

            {error && <div className="error">{error}</div>}
            {!imageURL && (
              <div className="weightAndType fw-bold">
                {t("uploadImage.maxWeightType")}
              </div>
            )}

            <div className="defaultImagesSection">
              <h4>{t("uploadImage.selectDefaultImage")}</h4>
              <div className="defaultImages">
                <img
                  src="/maisons-modeles/third_ImageCanvassDuplexPro-min.jpg"
                  alt={t("uploadImage.defaultImageAlt1", { defaultValue: "Default project image 1" })}
                  onClick={isAtLimit ? undefined : () =>
                    handleDefaultImageSelect(
                      "/maisons-modeles/ImageCanvassDuplexPro.jpg",
                      "ImageCanvassDuplexPro.jpg"
                    )
                  }
                  style={{ 
                    cursor: isAtLimit ? 'not-allowed' : 'pointer',
                    opacity: isAtLimit ? 0.5 : 1,
                    filter: isAtLimit ? 'grayscale(50%)' : 'none'
                  }}
                />
                <img
                  src="/maisons-modeles/third_ImageCanvassEtagesPro-min.jpg"
                  alt={t("uploadImage.defaultImageAlt2", { defaultValue: "Default project image 2" })}
                  onClick={isAtLimit ? undefined : () =>
                    handleDefaultImageSelect(
                      "/maisons-modeles/ImageCanvassEtagesPro.jpg",
                      "ImageCanvassEtagesPro.jpg"
                    )
                  }
                  style={{ 
                    cursor: isAtLimit ? 'not-allowed' : 'pointer',
                    opacity: isAtLimit ? 0.5 : 1,
                    filter: isAtLimit ? 'grayscale(50%)' : 'none'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

      </Modal.Body>
    </Modal>
  );
}

export default UploadImage;
